/**
 * Flow catalog. Renders each `PortalFlow` the contractor may launch as a card:
 * display name + description, a mono flowId chip, an amber "Interactive · may
 * ask you questions" badge when `maybeAsksQuestions`, and a Start CTA that
 * routes to the new-run composer. Disabled flows render greyed-out with the CTA
 * suppressed.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { PortalFlow } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { keys } from '../hooks/queryClient'
import { describeApiError, useI18n } from '../i18n'

export function FlowsPage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })

  return (
    <div className="page portal-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t.flows.title}</h1>
          <div className="page-sub">{t.flows.subtitle}</div>
        </div>
      </header>

      {flows.isLoading && (
        <div className="portal-loading">
          <Spinner size={18} /> {t.flows.loading}
        </div>
      )}

      {flows.isError && <Notice kind="danger">{describeApiError(t, flows.error)}</Notice>}

      {flows.data && flows.data.length === 0 && <Notice kind="info">{t.flows.emptyState}</Notice>}

      {flows.data && flows.data.length > 0 && (
        <div className="flow-grid">
          {flows.data.map((flow) => (
            <FlowCard
              key={flow.flowId}
              flow={flow}
              onStart={() => navigate(`/flows/${encodeURIComponent(flow.flowId)}/new`)}
              onHistory={() => navigate(`/conversations?flowId=${encodeURIComponent(flow.flowId)}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FlowCard({ flow, onStart, onHistory }: { flow: PortalFlow; onStart: () => void; onHistory: () => void }) {
  const { t } = useI18n()
  return (
    <div className={`flow-card card${flow.enabled ? '' : ' is-disabled'}`}>
      <div className="flow-card-body">
        <div className="flow-card-head">
          <h2 className="flow-card-title">{flow.displayName}</h2>
          {!flow.enabled && <Badge kind="disabled">{t.flows.unavailable}</Badge>}
        </div>
        {flow.description && <p className="flow-card-desc">{flow.description}</p>}
        <div className="flow-card-tags">
          <span className="chip mono">{flow.flowId}</span>
          {flow.maybeAsksQuestions && (
            <Badge kind="warning" dot>
              {t.flows.interactiveBadge}
            </Badge>
          )}
        </div>
      </div>
      <div className="flow-card-foot">
        <button type="button" className="btn btn-primary" disabled={!flow.enabled} onClick={onStart}>
          <Icon name="play" size={14} /> {t.flows.start}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onHistory}>
          <Icon name="history" size={13} /> {t.flows.history}
        </button>
      </div>
    </div>
  )
}
